/**
 * Compressão de fotos de evidência ANTES de sair do aparelho.
 *
 * Por que no cliente: o backend nunca toca nos bytes (o upload é um PUT direto
 * ao bucket via URL pré-assinada), então este é o único ponto onde dá para
 * encolher o arquivo. Comprimir aqui resolve de uma vez a banda de UPLOAD, o
 * espaço da fila offline no IndexedDB e — o que motivou tudo — a banda de
 * DOWNLOAD do B2, já que é este arquivo que todo mundo vai baixar depois.
 *
 * Regra de ouro: a evidência NUNCA se perde. Qualquer imprevisto (formato que o
 * navegador não decodifica, canvas indisponível, encoder ausente, resultado
 * maior que o original) devolve o arquivo original intacto.
 *
 * As funções de cálculo são puras e ficam separadas do canvas para poderem ser
 * testadas sem DOM — mesmo padrão de `assinaturaCanvas.ts`.
 */
import {
  ALVO_BYTES_EVIDENCIA,
  LARGURA_MAX_EVIDENCIA,
  LARGURA_MINIATURA,
  PASSO_QUALIDADE,
  QUALIDADE_JPEG_EVIDENCIA,
  QUALIDADE_MINIATURA,
  QUALIDADE_WEBP_EVIDENCIA,
  TENTATIVAS_COMPRESSAO,
} from "@caint/shared";

export interface Dimensoes {
  readonly largura: number;
  readonly altura: number;
}

/**
 * Dimensões de saída para caber num quadrado de `maxLado`, preservando a
 * proporção. NUNCA amplia: uma foto já pequena volta com as medidas originais
 * (ampliar só inventaria pixels e aumentaria o arquivo).
 */
export function calcularDimensoes(largura: number, altura: number, maxLado: number): Dimensoes {
  const maior = Math.max(largura, altura);
  if (maior <= maxLado || maior === 0) {
    return { largura, altura };
  }
  const fator = maxLado / maior;
  return {
    largura: Math.max(1, Math.round(largura * fator)),
    altura: Math.max(1, Math.round(altura * fator)),
  };
}

/** Tipos que vale a pena recomprimir. PDF e formatos que o canvas não decodifica
 *  passam intactos (um PDF re-encodado como imagem viraria lixo). */
const TIPOS_COMPRIMIVEIS = new Set(["image/jpeg", "image/png", "image/webp"]);

export function ehComprimivel(tipo: string): boolean {
  return TIPOS_COMPRIMIVEIS.has(tipo.toLowerCase());
}

export interface OpcoesCompressao {
  readonly maxLado?: number;
  readonly alvoBytes?: number;
  readonly qualidadeWebp?: number;
  readonly qualidadeJpeg?: number;
}

/** Foto pronta para enviar: a versão que fica como evidência e a miniatura da
 *  galeria. `miniatura` é `null` para PDF, para imagem que o navegador não
 *  decodifica, ou se a geração falhar — a UI cai na imagem cheia. */
export interface EvidenciaPreparada {
  readonly arquivo: File;
  readonly miniatura: File | null;
}

/**
 * Produz a foto comprimida E a miniatura numa passagem só.
 *
 * Decodificar é a parte cara (uma foto de 12 MP demora), então o bitmap é aberto
 * UMA vez e reaproveitado nas duas saídas — gerar a miniatura sai praticamente
 * de graça em cima da compressão que já ia acontecer.
 */
export async function prepararEvidencia(
  arquivo: File,
  opts: OpcoesCompressao = {},
): Promise<EvidenciaPreparada> {
  if (!ehComprimivel(arquivo.type)) {
    return { arquivo, miniatura: null };
  }
  const bitmap = await abrirBitmap(arquivo);
  if (!bitmap) {
    return { arquivo, miniatura: null };
  }
  try {
    return {
      arquivo: await comprimirDoBitmap(bitmap, arquivo, opts),
      miniatura: await gerarMiniaturaDoBitmap(bitmap, arquivo),
    };
  } catch {
    return { arquivo, miniatura: null };
  } finally {
    bitmap.close();
  }
}

/**
 * Redimensiona e recomprime a foto. Devolve o ORIGINAL sempre que comprimir não
 * for possível ou não compensar.
 */
export async function comprimirImagem(arquivo: File, opts: OpcoesCompressao = {}): Promise<File> {
  if (!ehComprimivel(arquivo.type)) {
    return arquivo;
  }
  const bitmap = await abrirBitmap(arquivo);
  if (!bitmap) {
    return arquivo;
  }
  try {
    return await comprimirDoBitmap(bitmap, arquivo, opts);
  } catch {
    return arquivo; // qualquer falha inesperada: mantém o original
  } finally {
    bitmap.close();
  }
}

async function comprimirDoBitmap(bitmap: ImageBitmap, arquivo: File, opts: OpcoesCompressao): Promise<File> {
  const maxLado = opts.maxLado ?? LARGURA_MAX_EVIDENCIA;
  const alvoBytes = opts.alvoBytes ?? ALVO_BYTES_EVIDENCIA;
  const qWebp = opts.qualidadeWebp ?? QUALIDADE_WEBP_EVIDENCIA;
  const qJpeg = opts.qualidadeJpeg ?? QUALIDADE_JPEG_EVIDENCIA;

  const destino = calcularDimensoes(bitmap.width, bitmap.height, maxLado);
  const jaPequeno = destino.largura === bitmap.width && destino.altura === bitmap.height;
  // Já cabe no limite E já está leve: recomprimir só gastaria bateria e
  // arriscaria perder qualidade sem ganho de banda.
  if (jaPequeno && arquivo.size <= alvoBytes) {
    return arquivo;
  }

  const melhor = await encodarComTentativas(bitmap, destino, {
    alvoBytes,
    qWebp,
    qJpeg,
    tetoBytes: arquivo.size,
  });
  if (!melhor) {
    return arquivo;
  }
  return new File([melhor.blob], renomear(arquivo.name, melhor.extensao), {
    type: melhor.blob.type,
    lastModified: arquivo.lastModified,
  });
}

/** Miniatura da galeria. Sem tentativas nem teto: já nasce na casa dos KB. */
async function gerarMiniaturaDoBitmap(bitmap: ImageBitmap, arquivo: File): Promise<File | null> {
  const destino = calcularDimensoes(bitmap.width, bitmap.height, LARGURA_MINIATURA);
  const encodado =
    (await desenharEEncodar(bitmap, destino, "image/webp", QUALIDADE_MINIATURA)) ??
    (await desenharEEncodar(bitmap, destino, "image/jpeg", QUALIDADE_MINIATURA));
  if (!encodado) {
    return null;
  }
  return new File([encodado.blob], renomear(arquivo.name, `thumb.${encodado.extensao}`), {
    type: encodado.blob.type,
    lastModified: arquivo.lastModified,
  });
}

/**
 * Decodifica respeitando a orientação EXIF. Isso não é detalhe: foto de celular
 * costuma vir "deitada" com uma tag EXIF mandando girar. Ao redesenhar no canvas
 * o EXIF se perde — sem `from-image` a evidência seria gravada de lado, para
 * sempre. Se o navegador não aceitar a opção, tenta sem ela; se nem assim
 * decodificar, devolve `null` e o chamador mantém o original.
 */
async function abrirBitmap(arquivo: File): Promise<ImageBitmap | null> {
  if (typeof createImageBitmap !== "function") {
    return null;
  }
  try {
    return await createImageBitmap(arquivo, { imageOrientation: "from-image" });
  } catch {
    try {
      return await createImageBitmap(arquivo);
    } catch {
      return null;
    }
  }
}

interface Encodado {
  readonly blob: Blob;
  readonly extensao: string;
}

/**
 * Tenta WebP e, se o navegador não souber encodar WebP, JPEG. Enquanto o
 * resultado passar do alvo, repete com menos qualidade — até `TENTATIVAS_COMPRESSAO`.
 * Só devolve algo se ficar MENOR que o original (`tetoBytes`).
 */
async function encodarComTentativas(
  bitmap: ImageBitmap,
  destino: Dimensoes,
  cfg: { alvoBytes: number; qWebp: number; qJpeg: number; tetoBytes: number },
): Promise<Encodado | null> {
  let melhor: Encodado | null = null;

  for (let tentativa = 0; tentativa < TENTATIVAS_COMPRESSAO; tentativa += 1) {
    const queda = tentativa * PASSO_QUALIDADE;
    const qualidadeWebp = Math.max(0.4, cfg.qWebp - queda);
    const qualidadeJpeg = Math.max(0.4, cfg.qJpeg - queda);

    let encodado = await desenharEEncodar(bitmap, destino, "image/webp", qualidadeWebp);
    // Safari antigo ignora o tipo pedido e devolve PNG — que, para foto, fica
    // MAIOR que o JPEG de origem. Nesse caso o JPEG é o caminho correto.
    if (!encodado) {
      encodado = await desenharEEncodar(bitmap, destino, "image/jpeg", qualidadeJpeg);
    }
    if (!encodado) {
      return melhor;
    }
    if (!melhor || encodado.blob.size < melhor.blob.size) {
      melhor = encodado;
    }
    if (encodado.blob.size <= cfg.alvoBytes) {
      break;
    }
  }

  // Comprimir que engorda não serve: melhor guardar o original.
  return melhor && melhor.blob.size < cfg.tetoBytes ? melhor : null;
}

/**
 * Desenha nas dimensões finais e encoda. Devolve `null` se o navegador não
 * produzir exatamente o tipo pedido (ver a armadilha do PNG silencioso acima).
 */
async function desenharEEncodar(
  bitmap: ImageBitmap,
  destino: Dimensoes,
  tipo: "image/webp" | "image/jpeg",
  qualidade: number,
): Promise<Encodado | null> {
  const canvas = criarCanvas(destino.largura, destino.altura);
  if (!canvas) {
    return null;
  }
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return null;
  }
  // JPEG não tem canal alfa: sem um fundo opaco, um PNG transparente viraria
  // preto. Pinta branco antes de desenhar.
  if (tipo === "image/jpeg") {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, destino.largura, destino.altura);
  }
  ctx.drawImage(bitmap, 0, 0, destino.largura, destino.altura);

  const blob = await paraBlob(canvas, tipo, qualidade);
  if (!blob || blob.type !== tipo || blob.size === 0) {
    return null;
  }
  return { blob, extensao: tipo === "image/webp" ? "webp" : "jpg" };
}

function criarCanvas(largura: number, altura: number): OffscreenCanvas | HTMLCanvasElement | null {
  if (typeof OffscreenCanvas === "function") {
    return new OffscreenCanvas(largura, altura);
  }
  if (typeof document === "undefined") {
    return null;
  }
  const canvas = document.createElement("canvas");
  canvas.width = largura;
  canvas.height = altura;
  return canvas;
}

function paraBlob(
  canvas: OffscreenCanvas | HTMLCanvasElement,
  tipo: string,
  qualidade: number,
): Promise<Blob | null> {
  if ("convertToBlob" in canvas) {
    return canvas.convertToBlob({ type: tipo, quality: qualidade }).catch(() => null);
  }
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), tipo, qualidade);
  });
}

/** Troca a extensão do nome para casar com o formato realmente gravado. */
export function renomear(nome: string, extensao: string): string {
  const base = nome.replace(/\.[^./\\]+$/, "");
  return `${base || "evidencia"}.${extensao}`;
}
